import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/hooks/useSeo";
import { api } from "@/lib/api";
import { HeroSearch } from "@/components/HeroSearch";
import { BookingModal } from "@/components/BookingModal";
import { FlightCard, airportName } from "@/components/FlightCard";
import { paxLabel } from "@/components/PassengerPicker";
import { AirlineLogos } from "@/components/AirlineLogos";
import { Loader2, ArrowLeftRight, Route } from "lucide-react";

export const parseLegs = (s) => (s || "").split(",").filter(Boolean).map((x) => { const [origin, destination, date] = x.split("_"); return { origin, destination, date: date || "" }; });

export default function Flights() {
  const { t, lang, money } = useApp();
  useSeo({ title: t.seo.flights[0], description: t.seo.flights[1] });
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [picks, setPicks] = useState({});

  const trip = params.get("trip") || "oneway";
  const origin = params.get("origin");
  const destination = params.get("destination");
  const date = params.get("date") || "";
  const returnDate = params.get("return") || "";
  const legsParam = params.get("legs") || "";
  const pax = { adults: Number(params.get("adults") || params.get("passengers") || 1), children: Number(params.get("children") || 0), infants: Number(params.get("infants") || 0) };
  const passengers = pax.adults + pax.children + pax.infants;
  const paxKey = `${pax.adults}-${pax.children}-${pax.infants}`;
  const hasQuery = trip === "multi" ? legsParam.length > 0 : !!(origin && destination);

  useEffect(() => {
    if (!hasQuery) return;
    setLoading(true); setPicks({}); setData(null);
    api.post("/flights/search", { trip_type: trip, origin: origin || "", destination: destination || "", date, return_date: returnDate, legs: parseLegs(legsParam), ...pax })
      .then((r) => setData(r.data)).catch(() => setData(trip === "oneway" ? [] : { legs: [] })).finally(() => setLoading(false));
  }, [trip, origin, destination, date, returnDate, legsParam, paxKey, hasQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const legs = useMemo(() => (trip === "oneway" ? null : data?.legs || []), [trip, data]);
  const chosen = legs ? legs.map((l, i) => l.flights.find((f) => f.id === picks[i])) : [];
  const allPicked = legs && legs.length > 0 && chosen.every(Boolean);
  const total = chosen.reduce((s, f) => s + (f?.total || 0), 0);

  const bookOne = (f) => setBooking({
    type: "flight", item_id: f.id, amount: f.total,
    title: `${f[`airline_${lang}`]} · ${airportName(f.origin, lang)} → ${airportName(f.destination, lang)}`,
    details: { depart: f.depart, arrive: f.arrive, date: f.date, origin: f.origin, destination: f.destination, ...pax },
  });
  const bookItinerary = () => setBooking({
    type: "flight", item_id: chosen[0].id, amount: total,
    title: `${t.flights.types[trip]} · ${chosen.map((f) => airportName(f.origin, lang)).join(" → ")} → ${airportName(chosen[chosen.length - 1].destination, lang)}`,
    details: { trip_type: trip, ...pax, date: chosen[0].date, segments: chosen.map((f) => ({ flight_id: f.id, origin: f.origin, destination: f.destination, date: f.date })) },
  });

  return (
    <div className="bg-sand min-h-screen">
      <div className="bg-tigris pt-8 pb-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-white text-2xl sm:text-3xl font-bold mb-6 font-display">{t.nav.flights}</h1>
          <HeroSearch />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-tigris" size={40} /></div>
        ) : !hasQuery ? (
          <div className="text-center py-20 text-slate-400">{t.flights.searchFirst}</div>
        ) : trip === "oneway" ? (
          !data || data.length === 0 ? <div className="text-center py-20 text-slate-400">{t.flights.noResults}</div> : (
            <>
              <h2 className="text-lg font-bold text-slate-700 mb-5">{t.flights.results} · {data.length}</h2>
              <div className="space-y-4">{data.map((f, i) => <FlightCard key={f.id} f={f} index={i} onBook={() => bookOne(f)} />)}</div>
            </>
          )
        ) : (
          <div className="space-y-10 pb-28">
            {legs.map((leg, i) => (
              <section key={i} data-testid={`leg-${i}`}>
                <h2 className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-tigris text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  {trip === "round" ? (i === 0 ? t.flights.outbound : t.flights.inbound) : `${t.flights.leg} ${i + 1}`} · {airportName(leg.origin, lang)} → {airportName(leg.destination, lang)}
                  {leg.date && <span className="text-xs text-slate-400 font-normal" dir="ltr">{leg.date}</span>}
                </h2>
                {leg.flights.length === 0 ? <p className="text-slate-400 text-sm">{t.flights.noResults}</p> : (
                  <div className="space-y-3">
                    {leg.flights.map((f, j) => <FlightCard key={f.id} f={f} index={j} selected={picks[i] === f.id} onSelect={() => setPicks({ ...picks, [i]: f.id })} />)}
                  </div>
                )}
              </section>
            ))}
            <div className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 shadow-2xl" data-testid="itinerary-bar">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-sm">
                  {trip === "round" ? <ArrowLeftRight size={18} className="text-gold" /> : <Route size={18} className="text-gold" />}
                  <div>
                    <div className="font-bold text-slate-700">{t.flights.types[trip]} · {chosen.filter(Boolean).length}/{legs.length} {t.flights.picked}</div>
                    <div className="text-xs text-slate-400">{paxLabel(pax, t.pax)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-2xl font-extrabold text-tigris" data-testid="itinerary-total">{money(total)}</div>
                  <button data-testid="book-itinerary-btn" disabled={!allPicked} onClick={bookItinerary}
                    className="btn-gold px-6 py-2.5 rounded-xl font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed">{t.common.bookNow}</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {booking && <BookingModal item={booking} onClose={() => setBooking(null)} />}
      <AirlineLogos />
    </div>
  );
}
